import { OpportunitiesContext } from 'pages/Opportunities/context/OpportunitiesContext';
import React, { useEffect } from 'react';
import { useContextSelector } from 'use-context-selector';
import {
  BodyCont,
  ButtonCont,
  CompanyImage,
  CompanyName,
  Description,
  DetailsWrapper,
  Header,
  HeaderCont,
  InfoWrapper,
  Label,
  MediaCont,
  MediaWrapper,
  Overview,
  PoweredButton,
  ProjectOverviewCont,
  ProjectRoleContainer,
  Summary,
} from './ProjectOverview.styles';
import { Button, Grid, Hidden, IconButton, Popover, Typography, useTheme } from '@material-ui/core';
import { LuShare2 } from 'react-icons/lu';
import { COLORS } from 'shared/constants/COLORS';
import { CustomTypography } from 'shared/elements/CustomTypography';
import { CASTING_LOGO, CASTING_LOGO_TEXT } from 'shared/constants/ASSETS_URL';
import { IoMdOpen } from 'react-icons/io';
import ProjectRoles from './ProjectRoles/ProjectRoles';
import { useProjectDetailsParser } from 'pages/Opportunities/components/OpportunitiesContent/ProjectItem/ProjectDetails/useProjectDetailsParser';
import MediaItem from 'pages/Opportunities/components/OpportunitiesContent/ProjectItem/MediaItems/MediaItem';
import { CustomTooltip } from 'pages/Opportunities/components/CustomTooltip/CustomTooltip';
import { CONFIG } from 'shared/config';
import { CheckMarkIcon } from '@room-match/shared-ui-components';
import { customSort } from 'shared/interfaces/utils/IMediaFileType';
import OpportunitiesLoader from 'pages/Opportunities/components/OpportunitiesContent/OpportunitiesLoader';
import SEO from '../SEO/SEO';
import { IProjectAttributes } from 'shared/interfaces/IProject';
import { ICastingRoleAttributes } from 'shared/interfaces/ICastingRole';

interface Props {
  projectId: string;
  roleId: string;
  projectData?: IProjectAttributes;
  rolesData?: ICastingRoleAttributes[];
}

const ProjectOverview: React.FC<Props> = ({ projectId, roleId, projectData, rolesData }) => {
  const isDark = useTheme().palette.type === 'dark';
  const fetchMoreProjectRoles = useContextSelector(OpportunitiesContext, (v) => v.fetchMoreProjectRoles);
  const openOpportunityModal = useContextSelector(OpportunitiesContext, (v) => v.openOpportunityModal);
  const {
    projectLoading,
    conflicts,
    usageRun,
    additionalDetails,
    referenceMedia,
    companyName,
    projectName,
    union,
    dates,
    location,
    roles,
    rate,
    release,
    type,
    casting_roles,
    unmatchedRoles,
    companyLogo,
    total_casting_roles_on_notice_board_count,
  } = useProjectDetailsParser(projectId, true, projectData);
  const setShrink = useContextSelector(OpportunitiesContext, (state) => state.setShrink);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };
  const copyLink = (event: React.MouseEvent<HTMLButtonElement>) => {
    // if (anchorEl !== null) return;
    // //copy to clipboard
    // navigator.clipboard.writeText(`${CONFIG.JOBS_APP_URL}/${projectId}`);
    // setAnchorEl(event.currentTarget);
    // setTimeout(() => {
    //   handleClose();
    // }, 1500);
    openOpportunityModal('share', `${CONFIG.JOBS_APP_URL}/${projectId}`, projectName);
  };

  const handleHome = () => {
    window.location.href = '/';
  };

  const SCROLL_ALLOWANCE = 200;
  const handleOnScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;

    const currentScroll = scrollHeight - scrollTop - SCROLL_ALLOWANCE;

    if (currentScroll < clientHeight) {
      fetchMoreProjectRoles();
    }
  };

  useEffect(() => {
    // console.log('ProjectOverview', projectId, roleId);
    setShrink(true);
  });

  if (projectLoading) return <OpportunitiesLoader />;

  if (projectName === '-' && companyName === '-')
    return (
      <div style={{ padding: 40 }}>
        <CustomTypography fontSize={20} fontWeight={600} style={{ textAlign: 'center' }}>
          Project Not Found
        </CustomTypography>
      </div>
    );

  console.log({ rolesData });

  return (
    <ProjectOverviewCont
      onScroll={handleOnScroll}
      style={{
        ...(projectData && {
          borderRadius: 8,
          border: `1px solid ${isDark ? COLORS.DARK_BORDER : COLORS.LIGHT_BORDER}`,
          maxHeight: 'calc(100vh - 150px)',
          overflowY: 'auto',
        }),
      }}
    >
      {projectData ? null : (
        <SEO title={projectName} description={additionalDetails} name={companyName} type="summary" />
      )}
      <HeaderCont
        style={{
          ...(projectData && {
            marginTop: 0,
            backgroundColor: 'transparent',
          }),
        }}
      >
        {projectData ? (
          <Header
            style={{
              ...(projectData && {
                padding: '10px 0px 16px',
                borderBottom: `2px solid ${isDark ? COLORS.DARK_BORDER : COLORS.LIGHT_BORDER}`,
                marginBottom: 20,
              }),
            }}
          >
            <Typography
              style={{
                fontSize: 20,
                fontWeight: 200,
              }}
            >
              Project Information
            </Typography>
          </Header>
        ) : (
          <Header>
            <CompanyImage src={companyLogo || CASTING_LOGO} />
            <CompanyName>{companyName}</CompanyName>
            <ButtonCont>
              <IconButton style={{ padding: 5 }} onClick={copyLink}>
                <LuShare2 color={COLORS.PRIMARY} />
              </IconButton>
              <Hidden smDown>
                <CustomTooltip title="Coming Soon!">
                  <Button
                    variant="contained"
                    color="primary"
                    style={{ fontWeight: 600, height: 35, alignSelf: 'center' }}
                  >
                    All Jobs from the Company
                  </Button>
                </CustomTooltip>
              </Hidden>
              <Hidden mdUp>
                <IconButton style={{ padding: 5 }}>
                  <IoMdOpen color={COLORS.PRIMARY} />
                </IconButton>
              </Hidden>
            </ButtonCont>
          </Header>
        )}
        <Summary container spacing={3}>
          <Grid item xs={12} lg={3} md={4} sm={6}>
            <Label fontWeight={500}>Project</Label>
            <Description>{projectName}</Description>
          </Grid>
          <Grid item xs={12} lg={3} md={4} sm={6}>
            <Label fontWeight={500}>Type</Label>
            <Description>{type}</Description>
          </Grid>
          <Grid item xs={12} lg={3} md={4} sm={6}>
            <Label fontWeight={500}>Work Dates</Label>
            <Description>{dates}</Description>
          </Grid>
          <Grid item xs={12} lg={3} md={4} sm={6}>
            <Label fontWeight={500}>Roles</Label>
            <Description>
              {total_casting_roles_on_notice_board_count ||
                projectData?.self_tapes_roles.data.filter((f) => f.attributes.self_tapes_requirements).length}
            </Description>
          </Grid>
          <Grid item xs={12} lg={3} md={4} sm={6}>
            <Label fontWeight={500}>Union</Label>
            <Description>{union}</Description>
          </Grid>
          <Grid item xs={12} lg={3} md={4} sm={6}>
            <Label fontWeight={500}>Work Location</Label>
            <Description>{location}</Description>
          </Grid>
          {rate !== '-' ? (
            <Grid item xs={12} lg={3} md={4} sm={6}>
              <Label fontWeight={500}>Rate</Label>
              <Description>{rate}</Description>
            </Grid>
          ) : null}
        </Summary>
        {projectData ? null : (
          <PoweredButton onClick={handleHome}>
            <img src={CASTING_LOGO} alt="logo" style={{ width: 40, marginRight: 10 }} />
            <CustomTypography fontSize={14} fontWeight={600}>
              Powered by Casting.com
            </CustomTypography>
          </PoweredButton>
        )}
      </HeaderCont>
      {
        <Overview
          style={{
            ...(projectData && {
              marginLeft: 20,
            }),
          }}
        >
          {projectData ? 'Conflicts/Usage' : 'Project Overview'}
        </Overview>
      }
      <BodyCont>
        <DetailsWrapper>
          {conflicts !== '-' ? (
            <InfoWrapper>
              <Label fontWeight={500}>Conflict</Label>
              <Description>{conflicts}</Description>
            </InfoWrapper>
          ) : null}
          <InfoWrapper>
            <Label fontWeight={500}>Usage/Run</Label>
            <Description>{usageRun}</Description>
          </InfoWrapper>
          {projectData ? null : (
            <InfoWrapper>
              <Label fontWeight={500}>Additional Details</Label>
              <Description>{additionalDetails}</Description>
            </InfoWrapper>
          )}
        </DetailsWrapper>

        {referenceMedia ? (
          <MediaWrapper>
            <Label>Media Reference</Label>
            <MediaCont>
              {referenceMedia.sort(customSort).map((m) => (
                <MediaItem key={m.id} file={m} />
              ))}
            </MediaCont>
          </MediaWrapper>
        ) : (
          <React.Fragment />
        )}
      </BodyCont>
      {projectData ? (
        <Overview
          style={{
            borderTop: `2px solid ${isDark ? COLORS.DARK_BORDER : COLORS.LIGHT_BORDER}`,
            paddingTop: 20,
          }}
        >
          Role Information
        </Overview>
      ) : null}
      <ProjectRoles
        castingRolesData={rolesData}
        projectName={projectName}
        projectId={projectId}
        roleId={roleId}
        companyName={companyName}
        totalRoles={total_casting_roles_on_notice_board_count}
      />
      {open ? (
        <Popover
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          open={open}
        >
          <div style={{ padding: 10, display: 'flex', gap: 5, alignItems: 'center' }}>
            <CheckMarkIcon htmlColor={COLORS.SUCCESS} fontSize="small" />
            <CustomTypography fontSize={14} style={{ color: COLORS.SUCCESS }} fontWeight={700}>
              Link Copied!
            </CustomTypography>
          </div>
        </Popover>
      ) : null}
    </ProjectOverviewCont>
  );
};

export default ProjectOverview;
